import { createContext, useContext } from 'react';
import type {
  DownloadQuery,
  DownloadPreview,
  DownloadProgress,
} from '@/types/download';
import type { Activity, ActivityType, VibrationMode } from '@/types/activity';
import type { Patient } from '@/types/patient';

export interface DownloadQueryContextValue {
  // State
  query: DownloadQuery;
  preview: DownloadPreview | null;
  isLoadingPreview: boolean;
  downloadProgress: DownloadProgress;
  allPatients: Patient[];
  allActivities: Activity[];
  selectedActivityIds: Set<string>;

  // Selection actions
  setPatients: (ids: string[]) => void;
  setActivityTypes: (types: ActivityType[]) => void;
  setVibrationModes: (modes: VibrationMode[]) => void;
  setDateRange: (from: string | null, to: string | null) => void;
  clearFilters: () => void;

  // Activity selection actions
  toggleActivitySelection: (id: string) => void;
  selectAllActivities: () => void;
  deselectAllActivities: () => void;

  // Data actions
  setAllPatients: (patients: Patient[]) => void;
  setAllActivities: (activities: Activity[]) => void;

  // Preview
  loadPreview: () => void;

  // Download
  setDownloadProgress: (progress: DownloadProgress) => void;

  // Derived
  hasSelection: boolean;
  canDownload: boolean;
  selectedPatients: Patient[];
  filteredActivities: Activity[];
  selectedActivities: Activity[];
  totalCount: number;
  selectedCount: number;
  patientCount: number;
}

export const DownloadQueryContext =
  createContext<DownloadQueryContextValue | null>(null);

export function useDownloadQuery() {
  const context = useContext(DownloadQueryContext);
  if (!context) {
    throw new Error('useDownloadQuery must be used within a DownloadQueryProvider');
  }
  return context;
}
